import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchAuctions } from "../../redux/slices/auctionsSlice";
import Pagination from "../../components/Common/Pagination";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "../../contexts/LanguageContext";
import {
  MagnifyingGlassIcon,
  ChevronDownIcon,
  EyeIcon
} from "@heroicons/react/24/outline";

function Auctions() {
  const dispatch = useDispatch();
  const { t, tStatus } = useTranslation();
  const { auctions, loading, error, total, totalPages, currentPage } = useSelector((state) => state.auctions);
  const [searchParams] = useSearchParams();
  const statusTab = (searchParams.get('status') || 'all').toLowerCase();
  const [filters, setFilters] = useState({
    status: statusTab === 'all' ? '' : statusTab,
    searchTerm: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });

  // Sync the local status filter whenever the topbar tab changes.
  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      status: statusTab === 'all' ? '' : statusTab,
    }));
  }, [statusTab]);

  const ITEMS_PER_PAGE = 10;

  // Debug logging
  console.log('Auctions Component State:', { 
    auctionsCount: auctions.length, 
    total, 
    loading, 
    error,
    filters 
  });

  // Fetch auctions when component mounts or filters change
  useEffect(() => {
    console.log('Fetching auctions with filters:', filters);
    dispatch(fetchAuctions({ 
      currentPage: 1,
      limit: ITEMS_PER_PAGE,
      ...filters
    }));
  }, [dispatch, filters]);

  // Handle pagination
  const handlePageChange = (page) => {
    dispatch(fetchAuctions({ 
      currentPage: page,
      limit: ITEMS_PER_PAGE,
      ...filters
    }));
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Format date and time
  const formatDateTime = (dateString) => {
    if (!dateString) return { date: 'N/A', time: 'N/A' };
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
  };

  // Get status badge color
  const getStatusBadge = (auctionStatus) => {
    switch (auctionStatus?.toLowerCase()) {
      case 'live':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'blocked':
        return 'bg-red-100 text-red-800';
      case 'upcoming':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get status dot color
  const getStatusDotColor = (auctionStatus) => {
    switch (auctionStatus?.toLowerCase()) {
      case 'live':
        return 'bg-green-500';
      case 'completed':
        return 'bg-blue-500';
      case 'blocked':
        return 'bg-red-500';
      case 'upcoming':
        return 'bg-yellow-500';
      default:
        return 'bg-gray-400';
    }
  };

  return (
    <div>
      <div className="bg-white rounded-[10px] border border-dashboardStroke shadow-[0px_9px_15px_rgba(0,0,0,0.07)] overflow-hidden">
        {/* Dark Header */}
        <div className="bg-dashboardDark h-16 px-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h2 className="text-white font-semibold text-lg">{t("auctions.title")}</h2>
            <span className="text-white font-semibold">({total || 0})</span>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 border border-white rounded">
              <MagnifyingGlassIcon className="w-[18px] h-[18px] text-white" />
              <input
                type="text"
                placeholder={t("auctions.searchPlaceholder")}
                value={filters.searchTerm}
                onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                className="bg-transparent text-white placeholder-white/70 text-sm font-light outline-none w-32"
              />
            </div>
            
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="bg-transparent border border-white rounded px-3 py-1.5 text-white text-sm font-light outline-none cursor-pointer"
            >
              <option value="" className="text-dashboardDark">{t("auctions.filter.allStatus")}</option>
              <option value="live" className="text-dashboardDark">{t("auctions.filter.live")}</option>
              <option value="completed" className="text-dashboardDark">{t("auctions.filter.completed")}</option>
              <option value="blocked" className="text-dashboardDark">{t("auctions.filter.blocked")}</option>
              <option value="upcoming" className="text-dashboardDark">{t("auctions.filter.upcoming")}</option>
            </select>
            
            <select
              value={`${filters.sortBy}-${filters.sortOrder}`}
              onChange={(e) => {
                const [sortBy, sortOrder] = e.target.value.split('-');
                handleFilterChange('sortBy', sortBy);
                handleFilterChange('sortOrder', sortOrder);
              }}
              className="bg-transparent border border-white rounded px-3 py-1.5 text-white text-sm font-light outline-none cursor-pointer"
            >
              <option value="createdAt-desc" className="text-dashboardDark">{t("auctions.sort.newestFirst")}</option>
              <option value="createdAt-asc" className="text-dashboardDark">{t("auctions.sort.oldestFirst")}</option>
              <option value="title-asc" className="text-dashboardDark">{t("auctions.sort.titleAZ")}</option>
              <option value="title-desc" className="text-dashboardDark">{t("auctions.sort.titleZA")}</option>
              <option value="startTime-desc" className="text-dashboardDark">{t("auctions.sort.startTimeLatest")}</option>
              <option value="startTime-asc" className="text-dashboardDark">{t("auctions.sort.startTimeEarliest")}</option>
            </select>
          </div>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-dashboardCoral"></div>
            <span className="ml-2 text-dashboardText">{t("auctions.loading")}</span>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-500">{error}</p>
          </div>
        ) : auctions.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-dashboardText">{t("auctions.noFound")}</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-dashboardStroke">
                    <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("auctions.col.title")}</th>
                    <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("auctions.col.seller")}</th>
                    <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("auctions.col.country")}</th>
                    <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("auctions.col.startTime")}</th>
                    <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("auctions.col.endTime")}</th>
                    <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("col.type")}</th>
                    <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("auctions.col.bids")}</th>
                    <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("auctions.col.lots")}</th>
                    <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("col.status")}</th>
                    <th className="px-4 py-4 text-left text-[17px] font-medium text-dashboardText">{t("col.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {auctions.map((auction, index) => {
                    const startTime = formatDateTime(auction.startTime);
                    const endTime = formatDateTime(auction.endTime);
                    
                    return (
                      <tr 
                        key={auction._id}
                        className={`border-b border-dashboardStroke hover:bg-gray-50 transition-colors ${
                          index === 0 ? "shadow-[inset_3px_0px_0px_#FF7C17]" : ""
                        }`}
                      >
                        <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                          <div className="max-w-32 truncate" title={auction.title}>
                            {auction.title || 'Untitled Auction'}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-base font-medium text-dashboardCoral">
                          {auction.seller?.name || auction.userId?.name || 'Unknown Seller'}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                          {auction.country?.countryName || 'N/A'}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                          <div>{startTime.date}</div>
                          <div className="text-xs text-gray-400">{startTime.time}</div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                          <div>{endTime.date}</div>
                          <div className="text-xs text-gray-400">{endTime.time}</div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                          {auction.bidderType || 'Standard'}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                          {auction.totalBids || 0}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                          {auction.lotIds?.length || auction.lots?.length || 0}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${getStatusBadge(auction.status)}`}>
                            <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${getStatusDotColor(auction.status)}`} />
                            {tStatus(auction.status)}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <Link 
                            to={`/users/${auction.userId?._id || auction.createdBy}?tab=auction&auctionId=${auction._id}`}
                            className="p-1 hover:bg-gray-100 rounded transition-colors inline-block"
                          >
                            <EyeIcon className="w-6 h-6 text-dashboardDark" strokeWidth={1.3} />
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-4 py-4 border-t border-dashboardStroke">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  total={total}
                  itemsPerPage={ITEMS_PER_PAGE}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Auctions;
